import { FC, memo, useMemo, useId, useRef, useCallback } from 'react'
import classNames from 'classnames'
import { Popover } from 'devextreme-react'
import { NavLink } from 'react-router-dom'

import { hashCode } from '@crew/utils'

import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'

import {
  CrewRetryImage,
  CrewRetryImageRetryType,
} from '../crewRetryImage/crewRetryImage'

import CrewLock from 'assets/icons/crewLock.svg'

// アバターの形状
export const CrewAvatarShapeType = {
  Circle: 'circle',
  Square: 'square',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CrewAvatarShapeType =
  (typeof CrewAvatarShapeType)[keyof typeof CrewAvatarShapeType]

// アバターのサイズ
export const CrewAvatarSize = {
  base: 'base',
  '3xs': '3xs',
  '2xs': '2xs',
  xs: 'xs',
  sm: 'sm',
  lg: 'lg',
  xl: 'xl',
  '2xl': '2xl',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CrewAvatarSize =
  (typeof CrewAvatarSize)[keyof typeof CrewAvatarSize]

type avatarProps = {
  displayName: string
  avatarImage?: string
  className?: string
  imageURL?: string
  shape?: CrewAvatarShapeType // 未指定の場合は、丸型がデフォルト
  size?: CrewAvatarSize // 未指定の場合は、baseがデフォルト
  isPrivate?: boolean
  cacheValue: string | number // キャッシュ対策用。基本は「ID+version」を指定。プロジェクト登録ダイアログのアバター初期表示時など明示的なentityIdがない場合は表示名等を指定する。
  redirectUrl?: string // url when click avatar
  showPresenceState?: boolean
  textPresenceState?: string
}

export const CrewAvatar: FC<avatarProps> = memo((props) => {
  // popoverのtarget指定のために useId で一意な値を生成する
  // https://ja.react.dev/reference/react/useId
  // useId()で生成されるIDにはコロン(:)が含まれるため、CSSセレクターとして安全な形式に変換しておく
  const popoverTargetId = useId().replace(/:/g, '_')

  const popoverRef = useRef<Popover>(null)

  const avatarBaseClassNames = useMemo(() => {
    return 'flex items-center justify-center relative shrink-0'
  }, [])

  const targetSizeClassNames = useMemo(() => {
    if (props.size === CrewAvatarSize.base || props.size === undefined) {
      return 'crew-avatar'
    } else if (props.size === CrewAvatarSize['3xs']) {
      return 'crew-avatar-3xs'
    } else if (props.size === CrewAvatarSize['2xs']) {
      return 'crew-avatar-2xs'
    } else if (props.size === CrewAvatarSize.xs) {
      return 'crew-avatar-xs'
    } else if (props.size === CrewAvatarSize.sm) {
      return 'crew-avatar-sm'
    } else if (props.size === CrewAvatarSize.lg) {
      return 'crew-avatar-lg'
    } else if (props.size === CrewAvatarSize.xl) {
      return 'crew-avatar-xl'
    } else if (props.size === CrewAvatarSize['2xl']) {
      return 'crew-avatar-2xl'
    } else {
      return ''
    }
  }, [props.size])

  const targetShapeClassNames = useMemo(() => {
    if (
      props.shape === CrewAvatarShapeType.Circle ||
      props.shape === undefined
    ) {
      // 丸型
      return 'rounded-full'
    } else if (props.shape === CrewAvatarShapeType.Square) {
      // 四角型
      return 'rounded-md'
    } else {
      return ''
    }
  }, [props.shape])

  const targetSrc = useMemo(() => {
    if (!props.imageURL) {
      return ''
    }

    // キャッシュ対策用の値が数値の場合は文字列に変換
    const cacheValue =
      typeof props.cacheValue === 'string'
        ? props.cacheValue
        : props.cacheValue.toString()

    // 画像URLにキャッシュ対策用のクエリパラメータを付与
    return `${props.imageURL}&cache=${hashCode(cacheValue)}`
  }, [props.imageURL, props.cacheValue])

  const titleAvatar = useMemo(() => {
    if (!props.showPresenceState) return props.displayName

    return `${props.displayName} / ${props.textPresenceState}`
  }, [props.showPresenceState, props.displayName, props.textPresenceState])

  // アバタークリック時にpopoverを閉じる
  const handleClickAvatar = useCallback(() => {
    if (popoverRef.current) {
      popoverRef.current.instance.hide()
    }
  }, [])

  const renderAvatar = useMemo(
    () => (
      // fragmentで囲うとpopoverの表示時に要素がちらついてしまうため、divで囲っている
      <div>
        <div
          // popover表示用の一意なid
          id={`crew-avatar-container-${popoverTargetId}`}
          // 在席状態表示がない場合はtitle属性を設定
          // 在席状態表示がある場合は、ここではなくpopover側で表示する
          title={!props.showPresenceState ? titleAvatar : undefined}
          className={classNames(avatarBaseClassNames, props.className)}
          onClick={handleClickAvatar}
        >
          {props.imageURL ? (
            <CrewRetryImage
              key={props.cacheValue}
              src={targetSrc}
              alt={props.displayName}
              className={classNames(
                targetShapeClassNames,
                targetSizeClassNames,
                'object-cover'
              )}
              retryType={CrewRetryImageRetryType.RetryWithSameUrl} // 取得に失敗したら同じURLでリトライする
            />
          ) : props.avatarImage ? (
            <img
              src={props.avatarImage}
              alt={props.displayName}
              className={classNames(
                targetShapeClassNames,
                targetSizeClassNames,
                'object-cover'
              )}
            />
          ) : (
            // imageURL, avatarImage のどちらも設定されていない場合は、アバターの文字のみ表示
            <div>{props.displayName.slice(0, 1)}</div>
          )}
          {props.isPrivate && (
            <img
              src={CrewLock}
              className="absolute -bottom-1 -right-1 crew-text-gray-2 w-4 h-4"
              alt="lock icon"
            />
          )}
        </div>
        {/** 在席状態表示 */}
        {props.showPresenceState ? (
          <CrewGuideToolTip
            ref={popoverRef}
            target={`#crew-avatar-container-${popoverTargetId}`}
          >
            {titleAvatar}
          </CrewGuideToolTip>
        ) : null}
      </div>
    ),
    [
      handleClickAvatar,
      popoverTargetId,
      props.showPresenceState,
      props.className,
      props.imageURL,
      props.cacheValue,
      props.displayName,
      props.avatarImage,
      props.isPrivate,
      titleAvatar,
      avatarBaseClassNames,
      targetSrc,
      targetShapeClassNames,
      targetSizeClassNames,
    ]
  )

  return (
    <>
      {props?.redirectUrl ? (
        <NavLink to={props.redirectUrl} end>
          {renderAvatar}
        </NavLink>
      ) : (
        renderAvatar
      )}
    </>
  )
})
